/**
 * RoutesArrivalsTable - Arrivals table for PopupRoutesPage.
 *
 * Renders the sortable arrivals table with vessel info, state icons,
 * and per-row action menus.
 */
import React from 'react';
import {
  Button,
  Card,
  ScrollArea,
  ScrollBar,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@ais-ui/ui-core';
import { cn, formatVesselName } from '@ais-ui/utils';
import {
  PiArrowRight,
  PiCheck,
  PiDotsThreeVerticalBold,
  PiWarning,
} from 'react-icons/pi';
import type { TrackResult } from '@ais-ui/api-spec/portserver';
import type { DensityClasses } from './shared/popupDensityClasses';
import { SortableHead } from './RoutesTableComponents';

const StateIcon: React.FC<{
  message: string;
  densityClasses: DensityClasses;
}> = ({ message, densityClasses }) => {
  if (message?.toLowerCase() === 'arrived')
    return (
      <PiCheck className={cn(densityClasses.icon.medium, 'text-green-600')} />
    );
  if (message?.toLowerCase() === 'departed')
    return (
      <PiArrowRight
        className={cn(densityClasses.icon.medium, 'text-blue-600')}
      />
    );
  if (message && message !== 'unknown')
    return (
      <PiWarning
        className={cn(densityClasses.icon.medium, 'text-yellow-600')}
      />
    );
  return <span className={densityClasses.text}>{message || '-'}</span>;
};

export interface RoutesArrivalsTableProps {
  arrivals: TrackResult[];
  sortColumn: string;
  sortAscending: boolean;
  onSort: (column: string) => void;
  onViewTarget: (arrival: TrackResult) => void;
  formatDateTime: (
    date: Date | null | undefined,
    hideSeconds?: boolean,
  ) => string;
  densityClasses: DensityClasses;
  messages: Record<string, string>;
  totalCount: number;
}

export const RoutesArrivalsTable: React.FC<RoutesArrivalsTableProps> = ({
  arrivals,
  sortColumn,
  sortAscending,
  onSort,
  onViewTarget,
  formatDateTime,
  densityClasses,
  messages,
  totalCount,
}) => (
  <Card className="flex-1 flex flex-col overflow-hidden mt-4 min-h-0">
    <div className="p-3 border-b flex-shrink-0">
      <span className={cn('text-muted-foreground', densityClasses.text)}>
        {totalCount}{' '}
        {messages.popupRoutesRows?.replace('{count}', '') || 'rows'}
      </span>
    </div>

    <ScrollArea className="flex-1 min-h-0">
      <Table container={false}>
        <TableHeader className="sticky top-0 bg-background">
          <TableRow>
            <TableHead className={cn('w-10', densityClasses.tableHeader)}>
              #
            </TableHead>
            <SortableHead
              column="name"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableName}
            </SortableHead>
            <SortableHead
              column="mmsi"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableMmsi}
            </SortableHead>
            <TableHead className={densityClasses.tableHeader}>
              {messages.popupRoutesTableImo}
            </TableHead>
            <SortableHead
              column="callsign"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableCallsign}
            </SortableHead>
            <SortableHead
              column="draught"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableDraught}
            </SortableHead>
            <SortableHead
              column="country"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableFlag}
            </SortableHead>
            <SortableHead
              column="eta"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableEta}
            </SortableHead>
            <SortableHead
              column="state"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableState}
            </SortableHead>
            <SortableHead
              column="etaErp"
              currentColumn={sortColumn}
              ascending={sortAscending}
              onSort={onSort}
              densityClasses={densityClasses}
            >
              {messages.popupRoutesTableEtaErp}
            </SortableHead>
            <TableHead className={densityClasses.tableHeader}></TableHead>
          </TableRow>
        </TableHeader>
        <TableBody>
          {arrivals.map((arrival, i) => {
            const ship = arrival.source?.ship;
            const rowKey = `${ship?.mmsi || 'unknown'}-${ship?.imo || ''}-${arrival.eta || i}`;
            const draught = ship?.draught || arrival.source?.draught;
            return (
              <TableRow key={rowKey} className="odd:bg-muted/50">
                <TableCell className={densityClasses.tableCell}>
                  {i + 1}
                </TableCell>
                <TableCell
                  className={cn(
                    densityClasses.tableCell,
                    'font-medium max-w-40 truncate',
                  )}
                >
                  {formatVesselName(ship?.name || 'Unknown')}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {ship?.mmsi || '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {ship?.imo || '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {ship?.callsign || '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {draught ? `${draught} m` : '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {ship?.country || '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {arrival.eta ? formatDateTime(new Date(arrival.eta)) : '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  <StateIcon
                    message={arrival.message || ''}
                    densityClasses={densityClasses}
                  />
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  {arrival.etaErp
                    ? formatDateTime(new Date(arrival.etaErp))
                    : '-'}
                </TableCell>
                <TableCell className={densityClasses.tableCell}>
                  <DropdownMenu modal={false}>
                    <DropdownMenuTrigger asChild>
                      <Button variant="ghost" className={densityClasses.button}>
                        <PiDotsThreeVerticalBold
                          className={densityClasses.icon.medium}
                        />
                      </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">
                      <DropdownMenuItem onClick={() => onViewTarget(arrival)}>
                        {messages.popupRoutesMenuViewTarget}
                      </DropdownMenuItem>
                    </DropdownMenuContent>
                  </DropdownMenu>
                </TableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <ScrollBar orientation="horizontal" />
    </ScrollArea>
  </Card>
);
